import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  writeBatch,
} from "firebase/firestore";
import { db } from "../../../firebase/firebase";

const money = (value) =>
  Number(value || 0).toLocaleString("en-AE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatTimestamp = (value) => {
  try {
    const date = value?.toDate ? value.toDate() : value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return "—";
    return date.toLocaleString("en-AE", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const shortCompany = (company) => {
  const text = String(company || "").toLowerCase();
  if (text.includes("comfort")) return "Comfort Line";
  if (text.includes("lolia")) return "Lolia Limousine";
  return company || "—";
};

async function deleteUpload(upload) {
  const reportDate = String(upload?.reportDate || "").trim();
  const companyKey = String(upload?.companyKey || "").trim();
  const uploadKey = String(upload?.uploadKey || upload?.id || "").trim();
  if (!reportDate || !companyKey || !uploadKey) {
    throw new Error("Upload history record is incomplete and cannot be deleted safely.");
  }

  const recordsRef = collection(db, "dailyBookingPortalEarnings", reportDate, "records");
  const snap = await getDocs(recordsRef);
  const matching = snap.docs.filter((d) => {
    const data = d.data() || {};
    return String(data.companyKey || "") === companyKey && (!data.uploadKey || String(data.uploadKey) === uploadKey);
  });

  const batch = writeBatch(db);
  matching.forEach((d) => batch.delete(d.ref));
  await batch.commit();
  await deleteDoc(doc(db, "dailyBookingPortalUploads", uploadKey));
  return matching.length;
}

export default function DailyBookingPortalUploadHistory({ company = "" }) {
  const [uploads, setUploads] = useState([]);
  const [show, setShow] = useState(false);
  const [historyCompany, setHistoryCompany] = useState("all");
  const [deletingKey, setDeletingKey] = useState("");

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "dailyBookingPortalUploads"), (snap) => {
      setUploads(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (company) setHistoryCompany(company);
  }, [company]);

  const visible = useMemo(() => [...uploads]
    .filter((u) => historyCompany === "all" || u.company === historyCompany || u.incomeCompany === historyCompany)
    .sort((a, b) => String(b.reportDate || "").localeCompare(String(a.reportDate || ""))), [uploads, historyCompany]);

  async function remove(upload) {
    const ok = window.confirm(
      `Delete this Booking Portal daily upload?\n\n${shortCompany(upload.company)} — ${upload.reportDate}\n${upload.sourceFileName || ""}\n\n` +
      `This removes ONLY Daily Income Report data. Car Sheets and monthly Manual Booking uploads are not changed.`
    );
    if (!ok) return;

    const key = upload.uploadKey || upload.id;
    setDeletingKey(key);
    try {
      const removed = await deleteUpload(upload);
      alert(`Deleted Booking Portal daily upload and ${removed} saved row(s).`);
    } catch (err) {
      console.error(err);
      alert(`Delete failed: ${err?.message || err}`);
    } finally {
      setDeletingKey("");
    }
  }

  return (
    <div style={{ marginTop: 20 }}>
      <button type="button" onClick={() => setShow((v) => !v)} style={historyButton}>
        {show ? "Hide Upload History" : "View Upload History"}
      </button>

      {show && (
        <div style={panel}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <div>
              <h3 style={{ margin: 0 }}>Booking Portal Daily Upload History</h3>
              <div style={{ marginTop: 4, color: "#64748b", fontSize: 14 }}>
                Each date is stored separately. Re-uploading the same company/date replaces that day only.
              </div>
            </div>
            <select value={historyCompany} onChange={(e) => setHistoryCompany(e.target.value)} style={filterInput}>
              <option value="all">All companies</option>
              <option value="Comfort Line Luxury Motor Vehicles Services LLC">Comfort Line</option>
              <option value="Lolia Limousine for Luxury Motor Vehicles Services LLC">Lolia Limousine</option>
            </select>
          </div>

          <div style={{ overflowX: "auto", marginTop: 14 }}>
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>Report Date</th>
                  <th style={th}>Company</th>
                  <th style={th}>Source File</th>
                  <th style={{ ...th, textAlign: "right" }}>Rows</th>
                  <th style={{ ...th, textAlign: "right" }}>Booking Portal Bank</th>
                  <th style={{ ...th, textAlign: "right" }}>Booking Portal Cash</th>
                  <th style={th}>Uploaded / Updated</th>
                  <th style={th}>Uploaded By</th>
                  <th style={{ ...th, textAlign: "center" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {!visible.length && (
                  <tr><td colSpan={9} style={{ padding: 18, textAlign: "center", color: "#64748b" }}>No Booking Portal daily uploads found.</td></tr>
                )}
                {visible.map((u) => {
                  const key = u.uploadKey || u.id;
                  return (
                    <tr key={key}>
                      <td style={td}><strong>{u.reportDate || "—"}</strong></td>
                      <td style={td}>{shortCompany(u.company || u.incomeCompany)}</td>
                      <td style={{ ...td, maxWidth: 300, wordBreak: "break-word" }}>{u.sourceFileName || "—"}</td>
                      <td style={{ ...td, textAlign: "right" }}>{Number(u.rowCount || 0)}</td>
                      <td style={{ ...td, textAlign: "right" }}>AED {money(u.bankTotal)}</td>
                      <td style={{ ...td, textAlign: "right" }}>AED {money(u.cashTotal)}</td>
                      <td style={td}>{formatTimestamp(u.updatedAt)}</td>
                      <td style={td}>{u.uploadedBy || "—"}</td>
                      <td style={{ ...td, textAlign: "center" }}>
                        <button type="button" onClick={() => remove(u)} disabled={deletingKey === key} style={deleteButton}>
                          {deletingKey === key ? "Deleting..." : "Delete"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const historyButton = { padding: "10px 16px", border: "1px solid #475569", borderRadius: 6, background: "#fff", color: "#0f172a", fontWeight: 700, cursor: "pointer" };
const panel = { marginTop: 12, padding: 16, border: "1px solid #cbd5e1", borderRadius: 8, background: "#fff" };
const filterInput = { padding: "8px 10px", border: "1px solid #cbd5e1", borderRadius: 6, background: "white" };
const table = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const th = { padding: "9px 8px", background: "#f1f5f9", color: "#334155", borderBottom: "1px solid #cbd5e1", textAlign: "left", whiteSpace: "nowrap" };
const td = { padding: "9px 8px", borderBottom: "1px solid #e2e8f0", verticalAlign: "top" };
const deleteButton = { padding: "7px 10px", border: "1px solid #fecaca", borderRadius: 6, background: "#fff1f2", color: "#b91c1c", fontWeight: 700, cursor: "pointer" };
